import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { buildCompanyForPdf } from '../../services/pdf/companyForPdf';
import {
    A4_GEOMETRY,
    cityFromAddress,
    createLetterhead,
    createSpaceGuard,
    drawFooters,
    drawPage1Header,
    FOOTER_RESERVE,
    longDate,
    type CompanyInfo,
} from '../../services/pdf/letterhead';
import { COLORS } from '../../services/pdf/pdfUtils';
import type { Anschluss, PdfKontext } from './planungIntegration';
import { datumDeutsch, type UnterlagenZeile } from './unterlagenLogic';

/**
 * Die Unterlagenanforderung als Geschaeftsbrief — EIN Blatt an EINEN
 * Lieferanten (Bauherr, Tragwerksplaner, Gutachter …) mit den Unterlagen, die
 * er liefern soll und die noch nicht vorliegen.
 *
 * ─── Was draufsteht und was nicht ─────────────────────────────────────────
 * Name, Zweck („wann benoetigt"), Vorlauf und Rechtsgrundlage — das braucht
 * der Empfaenger, um zu verstehen, was gemeint ist und bis wann. NICHT drauf:
 * die Bemerkung (interne Notiz des Koordinators, wie `bewertung` bei den
 * Fragen) und die SiGeKo-Relevanz (Begruendung fuer den Koordinator selbst).
 * Wurde eine Unterlage schon einmal angefordert, steht das mit dem ERSTEN
 * Datum dabei — so ist die erneute Anforderung als solche erkennbar.
 *
 * ─── Briefkopf ────────────────────────────────────────────────────────────
 * Ausschliesslich ueber `letterhead.ts`, wie die Klaerungsliste
 * (`generatorPlanungsfragen.ts`): Seite 1 mit Briefkopf, autoTable bekommt
 * `willDrawPage`, damit auch seine selbst angelegten Folgeseiten das
 * Briefpapier tragen; Fusszeilen ganz zum Schluss.
 *
 * Das Blatt setzt KEINEN Status. Ob es verschickt wurde, haelt der Nutzer
 * danach ausdruecklich fest (`markiereAlsAngefordert`).
 */

export const UNTERLAGEN_ANFORDERUNG_TITEL = 'Anforderung von Unterlagen';

const SCHRIFT = { titel: 15, untertitel: 10.5, text: 10, klein: 8 };

/** Feste Spaltenbreiten in mm; „Wann benoetigt" bekommt den Rest. */
const BREITEN = { nr: 9, unterlage: 48, vorlauf: 28, recht: 30 };

export interface UnterlagenAnforderungOptionen {
    projektName?: string;
    projektNummer?: string;
    bauvorhabenAdresse?: string;
    /** Empfaenger in Klartext, z. B. „Tragwerksplaner". */
    empfaengerName?: string;
    datumIso?: string;
    ort?: string;
    /** Nur fuer Tests: eigenes jsPDF-Objekt (Muster `__docFactory` der Generatoren). */
    __docFactory?: () => jsPDF;
}

const einzeilig = (v?: string | null): string =>
    (v == null ? '' : String(v)).replace(/\s*\n\s*/g, ', ').replace(/\s+/g, ' ').trim();

/** Die Tabellenzeilen — exportiert, damit pruefbar ist, was NICHT draufsteht. */
export const anforderungsZeilen = (zeilen: readonly UnterlagenZeile[]): string[][] =>
    (zeilen || []).map((z, i) => {
        const d = z.dokument;
        const zuerst = z.eintrag && z.eintrag.angefordert_am
            ? `\nerneute Anforderung — zuerst angefordert am ${datumDeutsch(z.eintrag.angefordert_am)}`
            : '';
        return [
            String(i + 1),
            `${z.name}${d && d.kurz ? ` (${d.kurz})` : ''}${zuerst}`,
            (d && d.wannBenoetigt) || '—',
            (d && d.vorlauf) || '—',
            d && d.rechtsgrundlage && d.rechtsgrundlage.length ? d.rechtsgrundlage.join('; ') : '—',
        ];
    });

const zeichne = (
    doc: jsPDF,
    zeilen: readonly UnterlagenZeile[],
    company: CompanyInfo,
    opt: UnterlagenAnforderungOptionen,
): void => {
    const geo = A4_GEOMETRY;
    const { margin, contentWidth, rightX } = geo;
    const [tp0, tp1, tp2] = COLORS.text.primary;
    const [ln0, ln1, ln2] = COLORS.section.line;

    const lh = createLetterhead(doc, company, geo);
    lh.stampCurrentBg();
    const { addressFieldY, rightColBottom } = drawPage1Header(doc, company, lh, geo);

    let y = 0;
    const ensureSpace = createSpaceGuard(lh, geo, () => y, v => { y = v; });
    const fussReserve = lh.hasStationery ? lh.botMargin : FOOTER_RESERVE;
    const platzFuer = (hoehe: number) => ensureSpace(hoehe + fussReserve);

    // ── Anschriftfeld ────────────────────────────────────────────────────
    let addrY = addressFieldY + 5;
    const empfaenger = einzeilig(opt.empfaengerName);
    doc.setTextColor(tp0, tp1, tp2);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    if (empfaenger) {
        (doc.splitTextToSize(empfaenger, 85) as string[]).forEach(z => {
            doc.text(z, margin, addrY);
            addrY += 5;
        });
    }

    // ── Ort und Datum ────────────────────────────────────────────────────
    y = empfaenger
        ? Math.max(addrY + 6, addressFieldY + 30, rightColBottom + 8)
        : Math.max(addressFieldY, rightColBottom + 8);
    const ort = einzeilig(opt.ort) || cityFromAddress(company ? company.address : undefined);
    const datumText = longDate(opt.datumIso || undefined);
    doc.setFontSize(SCHRIFT.untertitel);
    doc.text(ort ? `${ort}, ${datumText}` : datumText, rightX, y, { align: 'right' });
    y += 12;

    // ── Titel und Bezug ──────────────────────────────────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(SCHRIFT.titel);
    doc.text(UNTERLAGEN_ANFORDERUNG_TITEL, margin, y);
    y += 7;

    const bezug: string[] = [];
    const name = einzeilig(opt.projektName);
    const nummer = einzeilig(opt.projektNummer);
    if (name && nummer) bezug.push(`${name} · Projekt-Nr. ${nummer}`);
    else if (name || nummer) bezug.push(name || `Projekt-Nr. ${nummer}`);
    const bv = einzeilig(opt.bauvorhabenAdresse);
    if (bv) bezug.push(`Bauvorhaben: ${bv}`);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(SCHRIFT.untertitel);
    bezug.forEach(zeile => {
        (doc.splitTextToSize(zeile, contentWidth) as string[]).forEach(z => {
            doc.text(z, margin, y);
            y += 5;
        });
    });
    y += 4;

    // ── Anschreiben ──────────────────────────────────────────────────────
    const absatz = (text: string) => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(SCHRIFT.text);
        doc.setTextColor(tp0, tp1, tp2);
        (doc.splitTextToSize(text, contentWidth) as string[]).forEach(z => {
            platzFuer(5);
            doc.text(z, margin, y);
            y += 5;
        });
        y += 3;
    };

    absatz('Sehr geehrte Damen und Herren,');
    absatz(
        'für die Koordination nach Baustellenverordnung benötigen wir zu dem oben genannten Bauvorhaben '
        + `die folgende${zeilen.length === 1 ? ' Unterlage' : 'n Unterlagen'}. Die Angaben zu Zweck und Vorlauf `
        + 'sollen Ihnen die Zuordnung erleichtern.',
    );

    // ── Tabelle ──────────────────────────────────────────────────────────
    platzFuer(18);
    autoTable(doc, {
        startY: y,
        head: [['Nr.', 'Unterlage', 'Wofür / wann benötigt', 'Vorlauf', 'Rechtsgrundlage']],
        body: anforderungsZeilen(zeilen),
        theme: 'grid',
        // Ohne wiederholte Kopfzeile weiss der Leser auf Seite 2 nicht mehr,
        // welche Spalte der Vorlauf ist.
        showHead: 'everyPage',
        margin: lh.autoTableMargin(),
        // autoTable legt Folgeseiten SELBST an — ohne den Callback bliebe jede
        // davon ohne Briefpapier.
        willDrawPage: lh.willDrawPage,
        headStyles: {
            fillColor: [255, 255, 255],
            textColor: [tp0, tp1, tp2],
            fontSize: SCHRIFT.klein,
            fontStyle: 'bold',
            lineColor: [ln0, ln1, ln2],
            lineWidth: 0.2,
        },
        styles: {
            fontSize: SCHRIFT.klein,
            textColor: [tp0, tp1, tp2],
            lineColor: [ln0, ln1, ln2],
            lineWidth: 0.2,
            cellPadding: { top: 1.4, bottom: 1.4, left: 1.6, right: 1.6 },
            valign: 'top',
            // Nichts wird abgeschnitten — ein halber Zweck behauptet etwas
            // anderes als der ganze.
            overflow: 'linebreak',
        },
        columnStyles: {
            0: { cellWidth: BREITEN.nr, halign: 'center' },
            1: { cellWidth: BREITEN.unterlage, fontStyle: 'bold' },
            2: { cellWidth: 'auto' },
            3: { cellWidth: BREITEN.vorlauf },
            4: { cellWidth: BREITEN.recht },
        },
    });
    const ende = (doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable;
    y = (ende && typeof ende.finalY === 'number' ? ende.finalY : y) + 8;

    // ── Schluss ──────────────────────────────────────────────────────────
    absatz(
        'Bitte senden Sie uns die Unterlagen zu, sobald sie vorliegen, oder teilen Sie uns mit, wann sie zu '
        + 'erwarten sind. Liegt eine Unterlage aus Ihrer Sicht nicht vor oder ist sie nicht erforderlich, '
        + 'bitten wir um einen kurzen Hinweis mit Begründung.',
    );
    absatz('Mit freundlichen Grüßen');
    if (company && company.name) absatz(einzeilig(company.name));

    // Fusszeile und Seitenzahl zuletzt — vorher steht die Gesamtzahl nicht fest.
    drawFooters(doc, company, lh, geo);
};

export const generateUnterlagenAnforderungPdf = (
    zeilen: readonly UnterlagenZeile[],
    company: CompanyInfo,
    opt: UnterlagenAnforderungOptionen = {},
): jsPDF => {
    const doc = opt.__docFactory ? opt.__docFactory() : new jsPDF({ format: 'a4', unit: 'mm', compress: true });
    zeichne(doc, zeilen, company || {}, opt);
    return doc;
};

/**
 * Anschluss fuer die Oberflaeche — wirft nie (Muster `planungIntegration.ts`).
 * `buildCompanyForPdf` ist PFLICHT: rohe Stammdaten ergaeben ein Blatt ohne
 * Briefpapier, und das faellt erst beim Empfaenger auf.
 */
export const baueUnterlagenAnforderungPdf = (
    zeilen: readonly UnterlagenZeile[],
    kontext: PdfKontext,
    opt: UnterlagenAnforderungOptionen,
): Anschluss<jsPDF> => {
    if (!zeilen || !zeilen.length) {
        return { status: 'fehler', fehler: 'Für diesen Empfänger ist keine Unterlage offen.' };
    }
    try {
        const company = buildCompanyForPdf(
            kontext.company as never,
            kontext.quoteConfig as never,
            kontext.invoiceConfig as never,
        );
        return { status: 'ok', wert: generateUnterlagenAnforderungPdf(zeilen, company, opt) };
    } catch (e) {
        return { status: 'fehler', fehler: `Das PDF konnte nicht erzeugt werden: ${e instanceof Error ? e.message : String(e)}` };
    }
};

/** Dateiname ohne Sonderzeichen — er landet ggf. auch als Objektname im Storage. */
export const anforderungDateiname = (projektName: string, empfaenger: string, datumIso: string): string => {
    const sauber = (s: string) => (s || '')
        .replace(/[^\wÄÖÜäöüß\- ]+/g, '')
        .trim()
        .replace(/\s+/g, '_')
        .slice(0, 60);
    const teile = ['Unterlagenanforderung', sauber(projektName), sauber(empfaenger), (datumIso || '').slice(0, 10) || 'ohne-Datum'];
    return `${teile.filter(Boolean).join('_')}.pdf`;
};
